import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';

import useSponsored from 'Hooks/useSponsored';

const messages = defineMessages({
  besteversaving: {
    id: 'cardLabel.besteversaving',
    defaultMessage: 'Best Ever Saving',
  },
  lastchance: {
    id: 'cardLabel.lastchance',
    defaultMessage: 'Last Chance',
  },
  preorder: {
    id: 'cardLabel.preorder',
    defaultMessage: 'Pre-order',
  },
  preorderwithdate: {
    id: 'cardLabel.preorderwithdate',
    defaultMessage: 'Releases {date}',
  },
  earlyaccess: {
    id: 'cardLabel.earlyaccess',
    defaultMessage: 'Available {date}',
  },
  earlyaccessnow: {
    id: 'cardLabel.earlyaccessnow',
    defaultMessage: 'Available Now',
  },
  newrelease: {
    id: 'cardLabel.newrelease',
    defaultMessage: 'Released {date}',
  },
  flashdeal: {
    id: 'cardLabel.flashdeal',
    defaultMessage: 'Flash Deal',
  },
  stardeal: {
    id: 'cardLabel.stardeal',
    defaultMessage: 'Star Deal',
  },
  giveaway: {
    id: 'cardLabel.giveaway',
    defaultMessage: 'Giveaway',
  },
  justlaunched: {
    id: 'cardLabel.justlaunched',
    defaultMessage: 'Just Launched',
  },
  eBookBundle: {
    id: 'cardLabel.eBookBundle',
    defaultMessage: 'eBook Bundle',
  },
  inLibrary: {
    id: 'cardLabel.inLibrary',
    defaultMessage: 'In Library',
  },
  inSteamLibrary: {
    id: 'cardLabel.inSteamLibrary',
    defaultMessage: 'In Steam Library',
  },
  bundlefest: {
    id: 'cardLabel.bundlefest',
    defaultMessage: 'Bundlefest',
  },
  lowstock: {
    id: 'cardLabel.lowstock',
    defaultMessage: 'Low Stock',
  },
  sponsored: {
    id: 'cardLabel.sponsored',
    defaultMessage: 'Sponsored',
  },
});

export default function CardLabel({
  slug,
  bestDiscount,
  preorderDate,
  startingDate,
  endingDate,
  type,
  displayType,
  presale,
  discountPercent,
  flashDealCard,
  noReleaseDate,
  systemDiscount,
  labelOverride,
  earlyAccessDate,
  isStarDeal,
  isGiveaway,
}) {
  const { formatMessage, formatDate } = useIntl();
  const pathname = useSelector(state => state?.router?.location?.pathname) || '';
  const sponsoredHit = useSponsored()?.hit || null;
  const isSponsored = sponsoredHit && sponsoredHit?.slug === slug;

  const isSponsoredPath = () => {
    const sponsoredPaths = [
      'on-sale', 'latest-deals', 'new-releases', 'categories', 'publishers', 'franchises', 'collections',
    ];

    if (!pathname) return false;
    const segment = pathname.toLowerCase().split('/').filter(Boolean)[0];
    return sponsoredPaths.includes(segment);
  };

  const showSponsoredLabel = isSponsoredPath() && isSponsored;
  const timeSinceLaunch = (Date.now() / 1000) - startingDate;

  let lastChance = false;
  if (flashDealCard) {
    const dealSecondsRemaining = endingDate - (Date.parse(new Date()) / 1000);
    if (dealSecondsRemaining < 14400) lastChance = true;
  }

  const startDate = '2024-05-20T15:00:00.000Z';
  const endDate = '2024-05-27T15:00:00.000Z';
  let bundlefestLive = false;
  if (Date.now() > Date.parse(startDate) && Date.now() < Date.parse(endDate)) {
    bundlefestLive = true;
  }

  let label = null;
  let name = null;
  let showPreorderDate = false;

  if (lastChance) {
    label = 'lastchance';
    name = formatMessage(messages.lastchance);
  }

  if (bestDiscount) {
    label = 'bestdiscount';
    name = formatMessage(messages.besteversaving);
  }

  if ((preorderDate * 1000) > Date.now() && !presale && !earlyAccessDate) {
    label = 'preorder';
    name = formatMessage(messages.preorder);

    if (!noReleaseDate) {
      showPreorderDate = true;
      name = formatMessage(messages.preorderwithdate, {
        date: formatDate(new Date(preorderDate * 1000), { month: 'short', day: '2-digit' }),
      });
    }
  }

  if ((preorderDate * 1000) > Date.now() && earlyAccessDate) {
    label = 'earlyaccess';
    name = formatMessage(messages.earlyaccessnow);

    if ((earlyAccessDate * 1000) > Date.now()) {
      name = formatMessage(messages.earlyaccess, {
        date: formatDate(new Date(earlyAccessDate * 1000), { month: 'short', day: '2-digit' }),
      });
    }
  }

  if ((type === 'pick-and-mix' || type === 'bundle') && bundlefestLive) {
    label = 'bundlefest';
    name = formatMessage(messages.bundlefest);
  }

  if (
    !flashDealCard &&
    discountPercent > 0 &&
    (endingDate - startingDate < 176400) &&
    !systemDiscount
  ) {
    label = 'flashdeal';
    name = formatMessage(messages.flashdeal);
  }

  if (!flashDealCard && (displayType && displayType.includes('bundle')) && timeSinceLaunch < 86400 && label !== 'flashdeal') {
    label = 'landed';
    name = formatMessage(messages.justlaunched);
  }

  if (isStarDeal) {
    label = 'stardeal';
    name = formatMessage(messages.stardeal);
  }

  if (isGiveaway) {
    label = 'giveaway';
    name = formatMessage(messages.giveaway);
  }

  if (labelOverride === 'newrelease') {
    label = 'preorder';
    showPreorderDate = true;
    name = formatMessage(messages.newrelease, {
      date: formatDate(new Date(preorderDate * 1000), { month: 'short', day: '2-digit' }),
    });
  }

  if (slug === 'safe-in-our-world-charity-bundle-2024') {
    label = 'lowstock';
    name = formatMessage(messages.lowstock);
  }

  if (labelOverride === 'inLibrary') {
    label = 'inLibrary';
    name = formatMessage(messages.inLibrary);
  }

  if (labelOverride === 'inSteamLibrary') {
    label = 'inSteamLibrary';
    name = formatMessage(messages.inSteamLibrary);
  }

  //  sponsored at the bottom it's a paid placements and override all labels names
  if (showSponsoredLabel) {
    label = 'sponsored';
    name = formatMessage(messages.sponsored);
  }

  return (
    <React.Fragment>
      {label &&
        <div className={classNames('hitCardLabel', { 'pre-order-date': showPreorderDate })}>
          <div className={`d-flex label-${label}-container`}>
            <div className="label-content">
              {label === 'inLibrary' &&
                <FontAwesomeIcon icon={faBookOpen} className="mr-1" />
              }
              {label === 'inSteamLibrary' &&
                <img
                  src="https://cdn.fanatical.com/production/icons/drm/light/steam-icon.svg"
                  alt="Steam Logo"
                  className="d-inline svg-inline--fa fa-lg mr-1"
                />
              }
              {name}
            </div>
            <div className="label-slash" />
          </div>
          <div className={`label-triangle triangle-${label}`} />
        </div>
      }
    </React.Fragment>
  );
}

CardLabel.propTypes = {
  slug: PropTypes.string,
  bestDiscount: PropTypes.bool,
  preorderDate: PropTypes.number,
  earlyAccessDate: PropTypes.number,
  startingDate: PropTypes.number,
  endingDate: PropTypes.number,
  type: PropTypes.string,
  displayType: PropTypes.string,
  presale: PropTypes.bool,
  flashDealCard: PropTypes.bool,
  discountPercent: PropTypes.number,
  noReleaseDate: PropTypes.bool,
  labelOverride: PropTypes.string,
  systemDiscount: PropTypes.bool,
  isStarDeal: PropTypes.bool,
  isGiveaway: PropTypes.bool,
};

CardLabel.defaultProps = {
  slug: '',
  bestDiscount: false,
  preorderDate: 0,
  earlyAccessDate: 0,
  startingDate: 0,
  endingDate: 0,
  type: '',
  displayType: '',
  presale: false,
  flashDealCard: false,
  discountPercent: 0,
  noReleaseDate: false,
  collections: [],
  pathname: '',
  labelOverride: '',
  systemDiscount: false,
  isStarDeal: false,
  isGiveaway: false,
};
